<script setup lang="ts">
import { computed, onMounted, type ComputedRef } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { ENTITY_TYPE } from '@clearbanc/data-common-types';
import { VerificationType } from '@/types/verification-types';
import Status from '@/components/Status.vue';
import { RouteNames } from '@/data/routes';
import { type BusinessDetails } from '@/types/business.types';
import {
  VerificationService,
  type BusinessVerification,
} from '@clearbanc/service-classes/verification-service';

// -----
// Setup
// -----

const store = useStore();
const router = useRouter();

// -------------------
// Computed Properties
// -------------------

const business: ComputedRef<BusinessDetails> = computed(
  () => store.getters.business,
);
const businessVerification: ComputedRef<BusinessVerification | null> = computed(
  () => store.getters.businessVerification,
);

// -------
// Methods
// -------

const startVerification = async () => {
  if (businessVerification.value) {
    await processVerificationStatus();
    return;
  }

  try {
    await store.dispatchApiAction('POST_VERIFICATION', {
      type: VerificationType.BusinessInstantId,
      entityId: business.value.id,
      entityType: ENTITY_TYPE.BUSINESS,
      businessId: business.value.id,
    });
    await processVerificationStatus();
  } catch (_error) {
    router.push({ name: RouteNames.VERIFICATION.DOCUMENTS });
  }
};

const processVerificationStatus = async () => {
  if (
    VerificationService.hasPassedBusinessVerification(
      businessVerification.value,
    )
  ) {
    router.push({ name: RouteNames.VERIFICATION.COMPLETE });
    return;
  }

  if (
    VerificationService.hasSoftFailedBusinessVerification(
      businessVerification.value,
    )
  ) {
    router.push({ name: RouteNames.VERIFICATION.INFO });
    return;
  }

  if (
    VerificationService.hasHardFailedBusinessVerification(
      businessVerification.value,
    )
  ) {
    router.push({ name: RouteNames.VERIFICATION.DOCUMENTS });
    return;
  }

  router.push({
    name: RouteNames.VERIFICATION.DOCUMENTS,
  });
};

// ---------
// Lifecycle
// ---------

onMounted(() => {
  startVerification();
});
</script>

<template>
  <Status iconType="loading" text="Verifying Profiles..." />
</template>
